import { normalizeName } from '../shared/naming';
import { CONTROLLED_SERVICE_REGISTRY } from '../quality/controlled-services';
import { guessMeasureBy } from './service-catalog';
import { SERVICE_REGISTRY } from './service-registry';
import { STAGE_COLOR, STAGE_REGISTRY, type StageDiscipline } from './stage-registry';
import { guessAppliesTo, type AppliesTo } from './applies-to';
import type { MeasureBy } from './geometry';

/**
 * O catálogo global de serviços — o que a obra chama de **atividade** (Fase 21, ADR-134).
 *
 * As listas que o sistema já tinha viram uma só, semeada como cadastro global (`project_id`
 * nulo, RN-007): as etapas da obra (`STAGE_REGISTRY`, da preliminar à estrutura — Fase 23),
 * a planilha de disciplinas e serviços da construtora (`SERVICE_REGISTRY`, de acabamento) e
 * o acréscimo dos serviços controlados que as FVS pedem e a planilha não tem
 * (`CONTROLLED_SERVICE_REGISTRY`).
 *
 * Antes desta fase cada obra cadastrava os seus, e duas obras chamavam a mesma coisa por
 * dois nomes. Agora o cadastro é um, e a obra **escolhe** dele.
 *
 * ## A grafia é a da planilha, de propósito
 *
 * `INSTALACOES ELETRICAS E DADOS`, sem acento e em caixa alta, é como o cliente escreve —
 * e é essa string que as 33 fichas de verificação apontam hoje, por nome normalizado
 * (ADR-127). Arrumar a grafia aqui desfaria a ligação de todas elas em silêncio. Quando a
 * ficha passar a apontar por id (fatia 2), a grafia vira decisão de cadastro, e não de
 * compatibilidade.
 */

/**
 * As disciplinas que o cliente já chamava pelo nome da planilha.
 *
 * `Elétrica` e `Hidráulica` foram semeadas no começo do sistema com nome curto, e a
 * planilha as chama de outro jeito — o que fazia a mesma disciplina existir duas vezes,
 * uma com serviços e outra sem. A semente **renomeia a existente** em vez de criar a
 * segunda: o id não muda, então as frentes, alocações e conferências que apontam para ela
 * seguem apontando. O que muda é o rótulo.
 */
export const DISCIPLINE_RENAMES: readonly { readonly from: string; readonly to: string }[] = [
  { from: 'Elétrica', to: 'INSTALACOES ELETRICAS E DADOS' },
  { from: 'Hidráulica', to: 'INSTALACOES HIDROSSANITARIAS' },
];

/**
 * O nome com que uma disciplina do sistema é semeada **hoje**.
 *
 * O laço que renomeia alcança a base que já rodava; esta tradução alcança a que nasce
 * agora, e quem procura a disciplina pelo nome antigo. Sem ela, uma base nova ganhava
 * `Elétrica` e `INSTALACOES ELETRICAS E DADOS` lado a lado — a mesma disciplina duas
 * vezes, uma com frentes e outra com serviços.
 *
 * Quem fala com essas disciplinas **pelo nome** precisa passar por aqui, e são três: a
 * semente do navegador, a do servidor e o vínculo função ↔ disciplina. O eletricista que
 * procura `Elétrica` num banco onde ela já se chama outra coisa não acha nada — e nasce
 * sem disciplina, em silêncio.
 */
export function seededDisciplineName(name: string): string {
  const chave = normalizeName(name);
  return DISCIPLINE_RENAMES.find((troca) => normalizeName(troca.from) === chave)?.to ?? name;
}

/**
 * A unidade que cada medida implica (RN-232).
 *
 * `measureBy` diz de onde o número sai da geometria; a unidade diz em que ele é lido. Área
 * de parede e área de piso são m²; perímetro e rodapé são metro; concreto é m³. `UNIT` é o
 * que não deriva da geometria — conta-se. `MODEL` vem do IFC e traz a medida do arquivo,
 * então fica sem unidade até alguém escolher.
 */
export const UNIT_BY_MEASURE: Readonly<Record<MeasureBy, string | undefined>> = {
  WALL: 'm2',
  FACE: 'm2',
  FLOOR: 'm2',
  CEILING: 'm2',
  PERIMETER: 'm',
  SKIRTING: 'm',
  ESPALA: 'm',
  VOLUME: 'm3',
  UNIT: 'un',
  MODEL: undefined,
  COUNT: 'un',
  LENGTH: 'm',
  AREA: 'm2',
  BOTTOM: 'm2',
  CONTOUR: 'm',
  WIDTH: 'm',
  // O aço da peça (Fase 27): a única medida do desenho que se lê em quilo.
  ACO: 'kg',
  // As estacas marcadas para o serviço — contam-se, como a contagem (Fase 27, D4).
  MARKED: 'un',
  // O fundo mais a borda da laje, em m² (Fase 27, etapa 3).
  AREA_CONTOUR: 'm2',
};

export interface DefaultGlobalService {
  readonly disciplineName: string;
  readonly name: string;
  /** Palpite pelo nome, como na importação de planilha — corrigível no cadastro. */
  readonly measureBy: MeasureBy;
  /** A peça da medida (RN-256). Ausente só em volume e espala que o nome não resolve. */
  readonly appliesTo?: AppliesTo;
  /** Código da unidade em `DEFAULT_UNITS`; ausente quando a medida não implica nenhuma. */
  readonly unitCode?: string;
}

/**
 * As cores das disciplinas novas.
 *
 * A árvore da estrutura pinta cada disciplina, e quinze cinzas iguais fariam a obra inteira
 * parecer uma coisa só. A lista se repete quando acabar — é decoração, não identidade.
 */
const CORES = [
  '#8b6f47',
  '#6b7280',
  '#c2703d',
  '#d4a017',
  '#3b82c4',
  '#7c6fb0',
  '#4f9d69',
  '#b05b7c',
  '#5a8fa8',
  '#a8763f',
] as const;

/**
 * As disciplinas do catálogo, na ordem em que as fontes as trazem.
 *
 * A ordem vira `position`, e ela é a da execução da obra, e não a alfabética (RN-005): as
 * etapas primeiro — preliminar, fundação, contenção, estrutura —, e o acabamento depois. As
 * das etapas levam a cor da etapa; as outras, a paleta em rodízio.
 */
export function defaultGlobalDisciplines(): readonly { name: string; color: string }[] {
  const vistas = new Set<string>();
  const lista: { name: string; color: string }[] = [];
  for (const disciplina of STAGE_REGISTRY) {
    vistas.add(normalizeName(disciplina.name));
    lista.push({ name: disciplina.name, color: STAGE_COLOR[disciplina.stage] });
  }
  let rodizio = 0;
  for (const disciplina of [...SERVICE_REGISTRY, ...CONTROLLED_SERVICE_REGISTRY]) {
    const chave = normalizeName(disciplina.name);
    if (vistas.has(chave)) continue;
    vistas.add(chave);
    lista.push({ name: disciplina.name, color: CORES[rodizio % CORES.length]! });
    rodizio += 1;
  }
  return lista;
}

/**
 * As posições das disciplinas semeadas: as etapas nas primeiras, e o resto na ordem de antes.
 *
 * As disciplinas do sistema (`DEFAULT_DISCIPLINES`) são semeadas antes do catálogo e ficavam
 * com as primeiras posições — Fundação e Estrutural na frente, e a alvenaria, a elétrica e a
 * hidráulica logo atrás, que é a ordem da obra: a infraestrutura vai embutida na alvenaria.
 * Com as etapas (Fase 23), a preliminar e a fundação passam na frente de todas, e o resto
 * mantém a ordem relativa que tinha. É a mesma conta que a migração `0028` faz na base que
 * já existe: as novas nas primeiras dezesseis, e as outras empurradas dezesseis para baixo. O
 * ARRANQUE DO PILAR (Fase 28) é a décima sétima: a `0064` o põe logo depois do BLOCO e
 * empurra uma posição o que vinha depois — o mesmo lugar que esta conta lhe dá na base nova.
 */
export function systemDisciplinePosition(index: number): number {
  return STAGE_REGISTRY.length + index;
}

/** A posição do `index`-ésimo de `defaultGlobalDisciplines()` — ver `systemDisciplinePosition`. */
export function catalogDisciplinePosition(index: number, systemCount: number): number {
  return index < STAGE_REGISTRY.length ? index : systemCount + index;
}

/**
 * Os serviços do catálogo, com a medida palpitada e a unidade que ela implica.
 *
 * O serviço que as fontes trazem entra uma vez só: o acréscimo controlado existe justamente
 * para completar a planilha, e não para duplicá-la.
 *
 * Os das etapas trazem a unidade escrita — ferragem é kg, e nenhuma medida da geometria leva
 * a quilo — e nascem **por unidade**, sem palpite pelo nome. Nenhum deles sai do traçado de
 * parede e piso do quantitativo, e o palpite errava feio: "LIMPEZA DO FUNDO DO BLOCO" virava
 * área de parede por causa da palavra "bloco", e "MOVIMENTAÇÃO DE TERRA" em m³ viraria o
 * volume de concreto de pilar e viga. A quantidade é lançada; ligá-la ao modelo é outra fase.
 *
 * A exceção são os serviços das duas peças que a planta traça, e a disciplina diz qual —
 * sem palpite, porque está escrito no registro:
 *
 * - a concretagem (m³) **é** o volume da peça (RN-259);
 * - a fôrma e o escoramento (m²) são as **faces** da peça: quatro no pilar, três na viga
 *   (decidido com o usuário em 01/10/2026, migração `0048`);
 * - a ferragem (kg) — CORTE E DOBRA, MONTAGEM e INSTALAÇÃO — é o **aço** da peça: o volume
 *   dela vezes a taxa da disciplina (Fase 27, F5, migração `0053`);
 * - a ESTACAS mede a estaca posicionada na planta, serviço a serviço (`medidaDaEstaca`);
 * - o BLOCO e as VIGAS BALDRAME medem o bloco e o baldrame desenhados, também serviço a
 *   serviço (`medidaDoBlocoEBaldrame`);
 * - a LAJE DE FUNDAÇÃO e a LAJE medem o radier e a laje desenhados, pelo nome do serviço
 *   (`medidaDoRadier`, `medidaDaLaje`).
 *
 * A disciplina sem peça na planta — a preliminar, a LOCAÇÃO DA OBRA e, até a etapa 2 da Fase
 * 28, o ARRANQUE DO PILAR — nasce toda digitada (`UNIT`/`NENHUM`), mesmo na ferragem em kg: o
 * aço dela não tem volume de onde sair, e o do arranque vem pronto da planilha por peça.
 */
/**
 * A medida de um serviço de etapa **quando a disciplina é de uma peça** (PILARES, VIGAS).
 *
 * Pela unidade e pelo nome do registro, e não por palpite: o metro cúbico é o volume da
 * peça; a fôrma e o escoramento, em m², são as faces dela; o quilo é o aço dela (ADR-176). O
 * resto (o controle tecnológico) não sai do desenho. A mesma lista está nas migrações `0048`
 * e `0053`.
 */
function medidaDaPeca(servico: {
  readonly name: string;
  readonly unitCode: string;
}): MeasureBy | undefined {
  if (servico.unitCode === 'm3') return 'VOLUME';
  if (servico.unitCode === 'm2' && /f[oô]rma|escoramento/i.test(servico.name)) return 'FACE';
  if (servico.unitCode === 'kg') return 'ACO';
  return undefined;
}

/**
 * O "Mede por" padrão de cada serviço de ESTACAS (Fase 27, tabela do plano) — pelo **nome**, e
 * não pela unidade como o pilar e a viga: quatro serviços em "un" medem três coisas diferentes
 * (a contagem do arrasamento, as marcadas do controle e do teste), e dois em "m3" são volume de
 * peça. O nome que o registro não lista fica digitado — medir pelo palpite é pior do que não
 * medir (D17).
 *
 * - a ferragem, nos três tempos → o aço do **tipo** de cada estaca (`ACO`, RN-367);
 * - concretagem e movimentação de terra → o volume da estaca (π·d²/4 × comprimento);
 * - perfuração → o comprimento;
 * - arrasamento → quantas estacas (cada uma tem a cabeça cortada);
 * - controle tecnológico e teste de resistência → as marcadas para o serviço (D4, RN-365).
 */
const MEDIDA_DA_ESTACA: readonly (readonly [string, MeasureBy])[] = [
  ['CORTE E DOBRA DE FERRAGEM', 'ACO'],
  ['MONTAGEM DE FERRAGEM', 'ACO'],
  ['INSTALAÇÃO DE FERRAGEM', 'ACO'],
  ['CONCRETAGEM', 'VOLUME'],
  ['PERFURAÇÃO', 'LENGTH'],
  ['MOVIMENTAÇÃO DE TERRA', 'VOLUME'],
  ['ARRASAMENTO DE ESTACA', 'COUNT'],
  ['CONTROLE TECNOLÓGICO DO CONCRETO', 'MARKED'],
  ['TESTE DE RESISTÊNCIA DA ESTACA', 'MARKED'],
];

function medidaDaEstaca(servico: { readonly name: string }): MeasureBy | undefined {
  return medidaPorNome(MEDIDA_DA_ESTACA, servico);
}

/**
 * O "Mede por" padrão de cada serviço de BLOCO e de VIGAS BALDRAME (Fase 27, etapa 2, tabela do
 * plano) — pelo **nome**, como a estaca: o lastro está em m³ e mede a área de fundo (F8), e os
 * serviços em "un" são a contagem, e não o palpite de uma unidade.
 *
 * - movimentação de terra e concretagem → o volume do concreto;
 * - compactação e lastro → a **área de fundo** (o lastro fica em m³, e o índice do concreto é a
 *   espessura, F8);
 * - a fôrma nos três tempos e a impermeabilização → as faces laterais;
 * - a limpeza do fundo do bloco e o controle tecnológico → quantas peças;
 * - a ferragem, nos três tempos → o aço: volume × a taxa da disciplina (RN-367).
 *
 * A mesma tabela serve às duas disciplinas: a VIGAS BALDRAME só não tem a limpeza do fundo do
 * bloco. O nome que o registro não lista fica digitado (D17) — o REATERRO do BLOCO (Fase 28,
 * `0064`) inclusive: é a vala menos o bloco, e o desenho não sabe a vala.
 */
const MEDIDA_DO_BLOCO_E_BALDRAME: readonly (readonly [string, MeasureBy])[] = [
  ['MOVIMENTAÇÃO DE TERRA', 'VOLUME'],
  ['COMPACTAÇÃO', 'BOTTOM'],
  ['LASTRO DE CONCRETO', 'BOTTOM'],
  ['CONFECÇÃO DE FÔRMA', 'FACE'],
  ['MONTAGEM DE FÔRMA', 'FACE'],
  ['DESMONTAGEM DE FÔRMA', 'FACE'],
  ['LIMPEZA DO FUNDO DO BLOCO', 'COUNT'],
  ['CORTE E DOBRA DE FERRAGEM', 'ACO'],
  ['MONTAGEM DE FERRAGEM', 'ACO'],
  ['INSTALAÇÃO DE FERRAGEM', 'ACO'],
  ['CONCRETAGEM', 'VOLUME'],
  ['IMPERMEABILIZAÇÃO', 'FACE'],
  ['CONTROLE TECNOLÓGICO DO CONCRETO', 'COUNT'],
];

function medidaDoBlocoEBaldrame(servico: { readonly name: string }): MeasureBy | undefined {
  return medidaPorNome(MEDIDA_DO_BLOCO_E_BALDRAME, servico);
}

/**
 * O "Mede por" padrão de cada serviço da LAJE DE FUNDAÇÃO — o radier (Fase 27, etapa 3, tabela
 * do plano) — pelo **nome**: o REATERRO, em m³, não sai do desenho e continua digitado.
 *
 * - a regularização do terreno, a compactação e a impermeabilização → a **área** do contorno;
 * - a concretagem → o volume, com a espessura de cada região (RN-369);
 * - a fôrma (F9) → a **borda**: perímetro × espessura;
 * - o controle tecnológico → quantas peças;
 * - a ferragem, nos três tempos → o aço: volume × a taxa da disciplina (RN-367).
 */
const MEDIDA_DO_RADIER: readonly (readonly [string, MeasureBy])[] = [
  ['REGULARIZAÇÃO DO TERRENO', 'AREA'],
  ['COMPACTAÇÃO', 'AREA'],
  ['IMPERMEABILIZAÇÃO', 'AREA'],
  ['MONTAGEM DE FÔRMA', 'CONTOUR'],
  ['CORTE E DOBRA DE FERRAGEM', 'ACO'],
  ['MONTAGEM DE FERRAGEM', 'ACO'],
  ['INSTALAÇÃO DE FERRAGEM', 'ACO'],
  ['CONCRETAGEM', 'VOLUME'],
  ['CONTROLE TECNOLÓGICO DO CONCRETO', 'COUNT'],
];

function medidaDoRadier(servico: { readonly name: string }): MeasureBy | undefined {
  return medidaPorNome(MEDIDA_DO_RADIER, servico);
}

/**
 * O "Mede por" padrão de cada serviço da LAJE — a laje do pavimento (Fase 27, etapa 3) —, pelo
 * nome: os de "un" (instalações elétricas, laje pré-moldada, estrutura protendida) ficam
 * digitados, e o que a planta não conhece não se mede por palpite (D17).
 *
 * - a fôrma, nos três tempos → o **fundo mais a borda**, num número só (`AREA_CONTOUR`);
 * - o escoramento → a área do fundo;
 * - a concretagem → o volume, sem os shafts (RN-370);
 * - o controle tecnológico → quantas peças;
 * - a ferragem, nos três tempos → o aço: volume × a taxa da disciplina (RN-367).
 */
const MEDIDA_DA_LAJE: readonly (readonly [string, MeasureBy])[] = [
  ['CONFECÇÃO DE FÔRMA', 'AREA_CONTOUR'],
  ['MONTAGEM DE FÔRMA', 'AREA_CONTOUR'],
  ['DESMONTAGEM DE FÔRMA', 'AREA_CONTOUR'],
  ['ESCORAMENTO', 'AREA'],
  ['CORTE E DOBRA DE FERRAGEM', 'ACO'],
  ['MONTAGEM DE FERRAGEM', 'ACO'],
  ['INSTALAÇÃO DE FERRAGEM', 'ACO'],
  ['CONCRETAGEM', 'VOLUME'],
  ['CONTROLE TECNOLÓGICO DO CONCRETO', 'COUNT'],
];

function medidaDaLaje(servico: { readonly name: string }): MeasureBy | undefined {
  return medidaPorNome(MEDIDA_DA_LAJE, servico);
}

/**
 * O "Mede por" padrão de cada serviço da PILARES DE CONTENÇÃO (Fase 27, etapa 4, tabela do plano)
 * — pelo **nome**; a MOVIMENTAÇÃO DE TERRA, em m³, não sai do desenho e continua digitada.
 *
 * - a fôrma, nos três tempos → as **faces** do pilar de contenção (F6);
 * - a ferragem, nos três tempos → o aço: volume × a taxa da disciplina (RN-367);
 * - a concretagem → o volume; o controle tecnológico → quantos pilares;
 * - o MURO DE CONTENÇÃO e a CORTINA ATIRANTADA (F7) → a **face** do muro, em m²: são os dois
 *   únicos desta disciplina que medem outra peça (`pecaDoServico`).
 */
const MEDIDA_DA_CONTENCAO: readonly (readonly [string, MeasureBy])[] = [
  ['CONFECÇÃO DE FÔRMA', 'FACE'],
  ['MONTAGEM DE FÔRMA', 'FACE'],
  ['DESMONTAGEM DE FÔRMA', 'FACE'],
  ['CORTE E DOBRA DE FERRAGEM', 'ACO'],
  ['MONTAGEM DE FERRAGEM', 'ACO'],
  ['INSTALAÇÃO DE FERRAGEM', 'ACO'],
  ['CONCRETAGEM', 'VOLUME'],
  ['CONTROLE TECNOLÓGICO DO CONCRETO', 'COUNT'],
  ['MURO DE CONTENÇÃO', 'FACE'],
  ['CORTINA ATIRANTADA', 'FACE'],
];

/** Os serviços da disciplina do pilar de contenção que medem o **muro**, e não o pilar (F7). */
const SERVICOS_DO_MURO: readonly string[] = ['MURO DE CONTENÇÃO', 'CORTINA ATIRANTADA'];

function medidaDaContencao(servico: { readonly name: string }): MeasureBy | undefined {
  return medidaPorNome(MEDIDA_DA_CONTENCAO, servico);
}

/**
 * O "Mede por" padrão de cada serviço da ESCADAS (Fase 27, etapa 5, tabela do plano) — pelo
 * **nome**; a FABRICAÇÃO DE PRÉ-MOLDADOS e a MONTAGEM DE ESCADA, em "un", não saem do desenho de
 * concreto e continuam digitadas.
 *
 * - a fôrma, nos três tempos → a **fôrma** da escada: fundo inclinado, espelhos, laterais e fundo
 *   do patamar (`FACE`);
 * - o escoramento → a **projeção** em planta (`AREA`);
 * - a concretagem → o volume; o controle tecnológico → quantas escadas;
 * - a ferragem, nos três tempos → o aço: volume × a taxa da disciplina (RN-367).
 */
const MEDIDA_DA_ESCADA: readonly (readonly [string, MeasureBy])[] = [
  ['CONFECÇÃO DE FÔRMA', 'FACE'],
  ['MONTAGEM DE FÔRMA', 'FACE'],
  ['DESMONTAGEM DE FÔRMA', 'FACE'],
  ['ESCORAMENTO', 'AREA'],
  ['CORTE E DOBRA DE FERRAGEM', 'ACO'],
  ['MONTAGEM DE FERRAGEM', 'ACO'],
  ['INSTALAÇÃO DE FERRAGEM', 'ACO'],
  ['CONCRETAGEM', 'VOLUME'],
  ['CONTROLE TECNOLÓGICO DO CONCRETO', 'COUNT'],
];

function medidaDaEscada(servico: { readonly name: string }): MeasureBy | undefined {
  return medidaPorNome(MEDIDA_DA_ESCADA, servico);
}

/**
 * A peça que o serviço mede: a da disciplina, salvo o muro e a cortina, que são serviços da
 * PILARES DE CONTENÇÃO e medem o `MURO_CONTENCAO` (F7).
 */
function pecaDoServico(
  disciplina: { readonly element?: StageDiscipline['element'] },
  servico: { readonly name: string },
): StageDiscipline['element'] {
  if (
    disciplina.element === 'PILAR_CONTENCAO' &&
    SERVICOS_DO_MURO.some((nome) => normalizeName(nome) === normalizeName(servico.name))
  ) {
    return 'MURO_CONTENCAO';
  }
  return disciplina.element;
}

function medidaPorNome(
  tabela: readonly (readonly [string, MeasureBy])[],
  servico: { readonly name: string },
): MeasureBy | undefined {
  const chave = normalizeName(servico.name);
  return tabela.find(([nome]) => normalizeName(nome) === chave)?.[1];
}

/** A medida padrão do serviço da disciplina, pela peça que a disciplina é. */
function medidaDoServico(
  disciplina: { readonly element?: StageDiscipline['element'] },
  servico: { readonly name: string; readonly unitCode: string },
): MeasureBy | undefined {
  if (disciplina.element === 'ESTACA') return medidaDaEstaca(servico);
  if (disciplina.element === 'BLOCO' || disciplina.element === 'BALDRAME') {
    return medidaDoBlocoEBaldrame(servico);
  }
  if (disciplina.element === 'RADIER') return medidaDoRadier(servico);
  if (disciplina.element === 'LAJE') return medidaDaLaje(servico);
  if (disciplina.element === 'PILAR_CONTENCAO') return medidaDaContencao(servico);
  if (disciplina.element === 'ESCADA') return medidaDaEscada(servico);
  return medidaDaPeca(servico);
}

export function defaultGlobalServices(): readonly DefaultGlobalService[] {
  const vistos = new Set<string>();
  const lista: DefaultGlobalService[] = [];
  for (const disciplina of STAGE_REGISTRY) {
    for (const servico of disciplina.services) {
      vistos.add(`${normalizeName(disciplina.name)}|${normalizeName(servico.name)}`);
      const medida = medidaDoServico(disciplina, servico);
      const peca = medida === undefined ? undefined : pecaDoServico(disciplina, servico);
      lista.push({
        disciplineName: disciplina.name,
        name: servico.name,
        measureBy: peca === undefined || medida === undefined ? 'UNIT' : medida,
        appliesTo: peca ?? 'NENHUM',
        unitCode: servico.unitCode,
      });
    }
  }
  for (const disciplina of [...SERVICE_REGISTRY, ...CONTROLLED_SERVICE_REGISTRY]) {
    for (const nome of disciplina.services) {
      const chave = `${normalizeName(disciplina.name)}|${normalizeName(nome)}`;
      if (vistos.has(chave)) continue;
      vistos.add(chave);
      const measureBy = guessMeasureBy(nome, undefined);
      const appliesTo = guessAppliesTo(measureBy, nome, disciplina.name);
      const unitCode = UNIT_BY_MEASURE[measureBy];
      lista.push({
        disciplineName: disciplina.name,
        name: nome,
        measureBy,
        ...(appliesTo === undefined ? {} : { appliesTo }),
        ...(unitCode === undefined ? {} : { unitCode }),
      });
    }
  }
  return lista;
}
